<template>
  <v-dialog :model-value="show" @update:model-value="$emit('update:show', $event)" max-width="520" persistent>
    <v-card class="result-dialog" :class="resultClass">
      <v-card-text class="text-center pa-8">
        <v-icon :size="72" :color="iconColor" class="mb-4">
          {{ isSuccess ? 'mdi-check-circle' : 'mdi-close-circle' }}
        </v-icon>

        <h2 class="result-title">{{ title }}</h2>
        <p class="result-message">{{ message }}</p>

        <v-divider class="my-4"></v-divider>

        <div class="result-stats">
          <div class="stat-item">
            <div class="stat-value primary--text">{{ data?.total_rows || 0 }}</div>
            <div class="stat-label">Total registros</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value success--text">{{ data?.successful_rows || 0 }}</div>
            <div class="stat-label">Exitosos</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value" :class="hasFailed ? 'error--text' : 'grey--text'">
              {{ data?.failed_rows || 0 }}
            </div>
            <div class="stat-label">Fallidos</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value grey--text">{{ formatDuration(data?.duration_ms) }}</div>
            <div class="stat-label">Duración</div>
          </div>
        </div>

        <v-alert v-if="hasFailed && data?.errors?.length" type="warning" variant="tonal" class="mt-4" density="compact">
          Se encontraron {{ data.errors.length }} error(es). Revisa los detalles en el historial.
        </v-alert>
      </v-card-text>

      <v-card-actions class="justify-center pb-6">
        <v-btn color="primary" variant="tonal" @click="$emit('close')">
          Cerrar
        </v-btn>
        <v-btn v-if="!isSuccess" color="primary" variant="outlined" class="ml-2" @click="$emit('retry')">
          Intentar de nuevo
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  show: { type: Boolean, default: false },
  result: { type: Object, default: null },
})

defineEmits(['close', 'retry'])

const isSuccess = computed(() => props.result?.success === true)

const hasFailed = computed(() => (props.result?.data?.failed_rows || 0) > 0)

const data = computed(() => props.result?.data || null)

const resultClass = computed(() => ({
  'success-border': isSuccess.value,
  'error-border': !isSuccess.value,
}))

const iconColor = computed(() => (isSuccess.value ? 'success' : 'error'))

const title = computed(() => {
  if (isSuccess.value) return 'Importación completada'
  if (hasFailed.value) return 'Importación con errores'
  return 'Importación fallida'
})

const message = computed(() => {
  return props.result?.message || ''
})

const formatDuration = (ms) => {
  if (!ms) return '-'
  if (ms < 1000) return ms + 'ms'
  return (ms / 1000).toFixed(1) + 's'
}
</script>

<style scoped>
.result-dialog {
  border-radius: 20px !important;
}

.success-border {
  border-top: 4px solid #22c55e;
}

.error-border {
  border-top: 4px solid #ef4444;
}

.result-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0a192f;
  margin-bottom: 8px;
}

.result-message {
  color: #64748b;
  font-size: 0.95rem;
}

.result-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.stat-item {
  text-align: center;
  min-width: 80px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 4px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #e2e8f0;
}
</style>
